/**
 * Marker predicates for cross-layer error handling. Kept free of React and
 * any client-only imports so both server code (API routes, reportError) and
 * client components can share the same protocol.
 */

/**
 * Errors carrying a message worth showing the user verbatim, rather than being
 * replaced by a generic "couldn't be processed".
 */
export const asUserFacing = <T extends Error>(error: T): T => {
  (error as T & { userFacing?: boolean }).userFacing = true;
  return error;
};

export const isUserFacingError = (error: unknown): error is Error =>
  error instanceof Error &&
  (error as Error & { userFacing?: boolean }).userFacing === true;

/** Marks an error as already reported to Sentry, so handlers don't duplicate. */
export const markSentryReported = <T extends Error>(error: T): T => {
  (error as T & { sentryReported?: boolean }).sentryReported = true;
  return error;
};

export const wasSentryReported = (error: unknown): boolean =>
  error instanceof Error &&
  (error as Error & { sentryReported?: boolean }).sentryReported === true;

/**
 * Marks an error as already toasted to the user, so upstream catch-alls don't
 * stack a second, vaguer toast on top of the specific one.
 */
export const markUserNotified = <T extends Error>(error: T): T => {
  (error as T & { userNotified?: boolean }).userNotified = true;
  return error;
};

export const wasUserNotified = (error: unknown): boolean =>
  error instanceof Error &&
  (error as Error & { userNotified?: boolean }).userNotified === true;
